import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface KnowledgeGraphHealth {
  documentsProcessed: number;
  documentsTotal: number;
  entitiesCreated: number;
  vectorsGenerated: number;
  graphConnections: number;
  aiCapabilityThreshold: {
    personas: { required: number; current: number; ready: boolean };
    valueProps: { required: number; current: number; ready: boolean };
    competitiveIntel: { required: number; current: number; ready: boolean };
    campaigns: { required: number; current: number; ready: boolean };
  };
  lastUpdated: string;
}

export const KnowledgeGraphStatus: React.FC = () => {
  const navigate = useNavigate();
  const [health, setHealth] = useState<KnowledgeGraphHealth | null>(null);
  const [loading, setLoading] = useState(true);

  const loadKnowledgeGraphHealth = useCallback(async () => {
    try {
      setLoading(true);
      // Truth-based API call - NO FALLBACKS
      const stats = await apiFetch('/api/v1/documents/stats/organization');
      
      setHealth({
        documentsProcessed: stats.stats?.processed || 0,
        documentsTotal: stats.stats?.total || 0,
        entitiesCreated: stats.stats?.entities || 0,
        vectorsGenerated: stats.stats?.vectors || 0,
        graphConnections: stats.stats?.connections || 0,
        aiCapabilityThreshold: {
          personas: {
            required: 25,
            current: stats.stats?.processed || 0,
            ready: (stats.stats?.processed || 0) >= 25
          },
          valueProps: {
            required: 30,
            current: stats.stats?.processed || 0,
            ready: (stats.stats?.processed || 0) >= 30
          },
          competitiveIntel: {
            required: 50,
            current: stats.stats?.processed || 0,
            ready: (stats.stats?.processed || 0) >= 50
          },
          campaigns: {
            required: 100,
            current: stats.stats?.processed || 0,
            ready: (stats.stats?.processed || 0) >= 100
          }
        },
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to load knowledge graph health:', error);
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKnowledgeGraphHealth();
  }, [loadKnowledgeGraphHealth]);

  const getHealthScore = () => {
    if (!health) return 0;
    
    const metrics = [
      health.documentsProcessed > 0 ? 25 : 0,
      health.entitiesCreated > 0 ? 25 : 0,
      health.vectorsGenerated > 0 ? 25 : 0,
      health.graphConnections > 0 ? 25 : 0
    ];
    
    return metrics.reduce((sum, score) => sum + score, 0);
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getHealthLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Knowledge Graph status...</p>
        </div>
      </div>
    );
  }

  const healthScore = getHealthScore();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <nav className="flex mb-4" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2 text-sm">
                <li>
                  <button onClick={() => navigate('/dashboard')} className="text-blue-600 hover:text-blue-800">
                    Dashboard
                  </button>
                </li>
                <li className="text-gray-400">/</li>
                <li className="text-gray-900 font-medium">Knowledge Graph Status</li>
              </ol>
            </nav>
            
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">🧠 Knowledge Graph Status</h1>
                <p className="mt-2 text-sm text-gray-600">
                  Monitor AI readiness and knowledge graph health
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/upload-center')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  📤 Upload More Content
                </button>
                <button
                  onClick={() => navigate('/data-foundation')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  ⚙️ Manage Entities
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overall Health</p>
                <p className={`text-2xl font-bold ${getHealthColor(healthScore)}`}>
                  {healthScore}%
                </p>
                <p className={`text-sm ${getHealthColor(healthScore)}`}>
                  {getHealthLabel(healthScore)}
                </p>
              </div>
              <div className="text-3xl">🧠</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Documents Processed</p>
                <p className="text-2xl font-bold text-blue-600">
                  {health?.documentsProcessed || 0}
                </p>
                <p className="text-sm text-gray-500">
                  of {health?.documentsTotal || 0} total
                </p>
              </div>
              <div className="text-3xl">📄</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Vector Embeddings</p>
                <p className="text-2xl font-bold text-green-600">
                  {(health?.vectorsGenerated || 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">chunks embedded</p>
              </div>
              <div className="text-3xl">🔗</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Entity Connections</p>
                <p className="text-2xl font-bold text-purple-600">
                  {health?.graphConnections || 0}
                </p>
                <p className="text-sm text-gray-500">knowledge links</p>
              </div>
              <div className="text-3xl">🕸️</div>
            </div>
          </div>
        </div>

        {/* AI Capability Readiness */}
        <div className="bg-white rounded-lg shadow-sm border mb-8">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">🚀 AI Generation Readiness</h2>
            <p className="text-sm text-gray-600 mt-1">
              Track progress toward AI-powered content generation capabilities
            </p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Persona Generation */}
              <div className={`p-4 rounded-lg border-2 ${
                health?.aiCapabilityThreshold.personas.ready 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-yellow-200 bg-yellow-50'
              }`}>
                <div className="flex items-center mb-3">
                  <span className="text-2xl mr-2">🎭</span>
                  <h3 className="font-semibold">Persona Generation</h3>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Documents</span>
                    <span>{health?.aiCapabilityThreshold.personas.current || 0} / {health?.aiCapabilityThreshold.personas.required || 25}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        health?.aiCapabilityThreshold.personas.ready ? 'bg-green-600' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(100, ((health?.aiCapabilityThreshold.personas.current || 0) / (health?.aiCapabilityThreshold.personas.required || 25)) * 100)}%` }}
                    ></div>
                  </div>
                </div>
                {health?.aiCapabilityThreshold.personas.ready ? (
                  <button 
                    onClick={() => navigate('/projects')}
                    className="w-full bg-green-600 text-white text-sm py-2 rounded hover:bg-green-700"
                  >
                    Generate Personas →
                  </button>
                ) : (
                  <p className="text-xs text-gray-600">
                    Upload {(health?.aiCapabilityThreshold.personas.required || 25) - (health?.aiCapabilityThreshold.personas.current || 0)} more documents
                  </p>
                )}
              </div>

              {/* Value Propositions */}
              <div className={`p-4 rounded-lg border-2 ${
                health?.aiCapabilityThreshold.valueProps.ready 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-yellow-200 bg-yellow-50'
              }`}>
                <div className="flex items-center mb-3">
                  <span className="text-2xl mr-2">💎</span>
                  <h3 className="font-semibold">Value Propositions</h3>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Documents</span>
                    <span>{health?.aiCapabilityThreshold.valueProps.current || 0} / {health?.aiCapabilityThreshold.valueProps.required || 30}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        health?.aiCapabilityThreshold.valueProps.ready ? 'bg-green-600' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(100, ((health?.aiCapabilityThreshold.valueProps.current || 0) / (health?.aiCapabilityThreshold.valueProps.required || 30)) * 100)}%` }}
                    ></div>
                  </div>
                </div>
                {health?.aiCapabilityThreshold.valueProps.ready ? (
                  <button 
                    onClick={() => navigate('/content-generation')}
                    className="w-full bg-green-600 text-white text-sm py-2 rounded hover:bg-green-700"
                  >
                    Generate Value Props →
                  </button>
                ) : (
                  <p className="text-xs text-gray-600">
                    Upload {(health?.aiCapabilityThreshold.valueProps.required || 30) - (health?.aiCapabilityThreshold.valueProps.current || 0)} more documents
                  </p>
                )}
              </div>

              {/* Competitive Intelligence */}
              <div className={`p-4 rounded-lg border-2 ${
                health?.aiCapabilityThreshold.competitiveIntel.ready 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-yellow-200 bg-yellow-50'
              }`}>
                <div className="flex items-center mb-3">
                  <span className="text-2xl mr-2">🔍</span>
                  <h3 className="font-semibold">Competitive Intel</h3>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Documents</span>
                    <span>{health?.aiCapabilityThreshold.competitiveIntel.current || 0} / {health?.aiCapabilityThreshold.competitiveIntel.required || 50}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        health?.aiCapabilityThreshold.competitiveIntel.ready ? 'bg-green-600' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(100, ((health?.aiCapabilityThreshold.competitiveIntel.current || 0) / (health?.aiCapabilityThreshold.competitiveIntel.required || 50)) * 100)}%` }}
                    ></div>
                  </div>
                </div>
                {health?.aiCapabilityThreshold.competitiveIntel.ready ? (
                  <button 
                    onClick={() => navigate('/brand-intelligence')}
                    className="w-full bg-green-600 text-white text-sm py-2 rounded hover:bg-green-700"
                  >
                    Generate Intel →
                  </button>
                ) : (
                  <p className="text-xs text-gray-600">
                    Upload {(health?.aiCapabilityThreshold.competitiveIntel.required || 50) - (health?.aiCapabilityThreshold.competitiveIntel.current || 0)} more documents
                  </p>
                )}
              </div>

              {/* Campaign Intelligence */}
              <div className={`p-4 rounded-lg border-2 ${
                health?.aiCapabilityThreshold.campaigns.ready 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-yellow-200 bg-yellow-50'
              }`}>
                <div className="flex items-center mb-3">
                  <span className="text-2xl mr-2">🚀</span>
                  <h3 className="font-semibold">Campaign Intel</h3>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Documents</span>
                    <span>{health?.aiCapabilityThreshold.campaigns.current || 0} / {health?.aiCapabilityThreshold.campaigns.required || 100}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        health?.aiCapabilityThreshold.campaigns.ready ? 'bg-green-600' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(100, ((health?.aiCapabilityThreshold.campaigns.current || 0) / (health?.aiCapabilityThreshold.campaigns.required || 100)) * 100)}%` }}
                    ></div>
                  </div>
                </div>
                {health?.aiCapabilityThreshold.campaigns.ready ? (
                  <button 
                    onClick={() => navigate('/performance-analytics')}
                    className="w-full bg-green-600 text-white text-sm py-2 rounded hover:bg-green-700"
                  >
                    Advanced Campaigns →
                  </button>
                ) : (
                  <p className="text-xs text-gray-600">
                    Upload {(health?.aiCapabilityThreshold.campaigns.required || 100) - (health?.aiCapabilityThreshold.campaigns.current || 0)} more documents
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">📋 Recommended Next Steps</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
              <button 
                onClick={() => navigate('/upload-center')}
                className="text-blue-800 hover:underline"
              >
                Upload more partner & vendor content
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
              <button 
                onClick={() => navigate('/data-foundation')}
                className="text-green-800 hover:underline"
              >
                Review entity configurations
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
              <button 
                onClick={() => navigate('/projects')}
                className="text-purple-800 hover:underline"
              >
                Start generating personas & campaigns
              </button>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 System Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Processing Status</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>• Documents in queue: {(health?.documentsTotal || 0) - (health?.documentsProcessed || 0)}</p>
                <p>• Processing rate: ~5 docs/minute</p>
                <p>• Vector store: {health?.vectorsGenerated ? 'Active' : 'Initializing'}</p>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Knowledge Graph</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>• Entities: {health?.entitiesCreated || 0} created</p>
                <p>• Connections: {health?.graphConnections || 0} mapped</p>
                <p>• Relationships: Active indexing</p>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Last Updated</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>{health?.lastUpdated ? new Date(health.lastUpdated).toLocaleString() : 'Never'}</p>
                <button 
                  onClick={loadKnowledgeGraphHealth}
                  className="text-blue-600 hover:underline"
                >
                  Refresh Status
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
